import React from 'react';

import type { OrderByItem } from './common';
import type { Company, CompanyDTO } from './company';
import type { INVITE_MODULES } from './permission';
import { VAT_TREATMENTS } from './drc';

export interface BACSFormat {
  system_name: string;
  name: string;
  description: string;
}

export interface HMRCSetting {
  name: string;
  tax_office_number: string;
  tax_office_ref: string;
  accounting_office_ref: string;
  utr: string;
  sender: string;
  sender_id: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_telephone: string;
  contact_fax: string;
  default: boolean;
  app_settings: boolean;
  deleted: boolean;
  company_id?: number;
  password: string;
}

export interface CISSubcontractor {
  effective_date: string;
  trading_name: string;
  unique_tax_reference: string;
  business_type: string;
  vat_registered: boolean;
  vat_registration_number: string;
  payment_method: string;
  pay_frequency: string;
  first_name: string;
  last_name: string;
  ni_number: string;
  telephone: string;
  region: string;
  address: string;
  bank_account: {
    account_name: string;
    account_number: string;
    sort_code: string;
  };
  company_name?: string;
  company_registration_number?: string;
}

export interface PaySchedule {
  custom_contract?: any;
  uuid?: string;
  name: string;
  company?: any;
  pay_frequency?: string;
  pay_weekday?: any;
  pay_last_workday?: boolean | null | string;
  created_at?: string;
  updated_at?: string;
  own_hmrc_settings?: boolean;
  email_pay_statement?: string | Date | null;
  sms_pay_statement?: string | Date | null;
  emailWeekday?: string;
  SMSWeekday?: string;
  outsourced?: boolean;
  payroll_fee_base?: number;
  payroll_fee_commission?: number;
  payroll_paid_by_worker?: boolean | string;
}

export interface TimesheetLine {
  adjustments: number;
  error: { message: string };
  expenses: number;
  basic_units: number;
  mile_rate: number;
  miles: number;
  overtime_units: number;
  overtime_rate: number;
  basic_rate: number;
  total: number;
  vat: number;
  worker_reference: string;
  employee: {
    first_name: string;
    last_name: string;
    ni_number: string;
    uuid: string;
    worker_reference: string;
  };
  basic: number;
  cis_deductions: number;
  employee_first_name: string;
  employee_id: string;
  employee_last_name: string;
  employee_ni_number: string;
  gross: number;
  mileage: number;
  net: number;
  overtime: number;
  payroll_fee: number;
  sent_to_hmrc: boolean;
  paid: boolean;
  uuid: string;
}

export interface EnrichedTimesheetLine extends TimesheetLine {
  total_pay: number;
  pay_scheme: string;
  tax_week_start: number;
  tax_week_end: number;
  tax_year_start: number;
  tax_year_end: number;
  payment_date: string;
  start_date: string;
  end_date: string;
  company: CompanyDTO;
}

export interface Timesheet {
  pay_run_id: string;
  name: string;
  week: number;
  schedule: string;
  pay_schedule_id: string;
  pay_schedule: PaySchedule;
  company?: CompanyDTO;
  status: string;
  modified: string;
  created: string;
  workers: number;
  gross: number;
  end_date: string;
  payment_date: string;
  start_date: string;
  tax_week_end: number;
  tax_week_start: number;
  tax_year_end: number;
  tax_year_start: number;
  submitted_by: {
    first_name: string;
    last_name: string;
    uuid: string;
  };
}

export interface Payrun {
  pay_run_id: string;
  /** The pay schedule's name — the list identifies the schedule by name, not id. */
  name: string;
  /** Present on newer responses; fall back to matching the schedule by name. */
  outsourced?: boolean;
  week: number;
  payment_date: string;
  schedule: string;
  status: string;
  workers: number;
  gross: number;
  gross_unpaid: number;
  net: number;
  net_unpaid: number;
  sms_notified: boolean;
  email_notified: boolean;
}

export interface PayrunLine {
  uuid: string;
  first_name: string;
  last_name: string;
  ni_number: string;
  worker_reference: string;
  pay_scheme: string;
  basic: number;
  basic_unpaid: number;
  overtime: number;
  overtime_unpaid: number;
  mileage: number;
  mileage_unpaid: number;
  cis_deductions: number;
  cis_deductions_unpaid: number;
  vat: number;
  vat_unpaid: number;
  /**
   * Reverse charge, optional because the pay-run read is not documented as carrying it — only
   * the workbook export and the pay-statement routes are. The grid shows the DRC column only
   * when the rows actually arrive with a treatment, so this degrades to the pre-DRC view.
   * TODO(VYCE-DRC): make these required once the gateway contract confirms them.
   */
  drc_vat?: string;
  drc_vat_unpaid?: string;
  vat_treatment?: VAT_TREATMENTS;
  vat_rate?: string | null;
  net: number;
  net_unpaid: number;
  gross: number;
  gross_unpaid: number;
  employee_id: string;
  pay_run_id: string;
  paid: boolean;
  partly_paid: boolean;
  sms_notified: boolean;
  partly_sms_notified: boolean;
  email_notified: boolean;
  partly_email_notified: boolean;
  verification: {
    status: boolean;
    errors: any[];
    meta: any;
  };
}

export interface TimesheetTotals {
  gross: number;
  net?: number;
  payroll_fee?: number;
  workers: number;
}

/** Calculation notes for the pay run / timesheet tables, keyed by the field a column or total shows. */
export interface TableHovers {
  lines?: Record<string, string>;
  totals?: Record<string, string>;
}

export interface PayrunTotals {
  gross: number;
  net: number;
  payroll_fee: number;
  workers: number;
  gross_unpaid: number;
  net_unpaid: number;
  total_net: number;
  total_gross: number;
  /** The run's reverse-charge liability, split like the lines. Optional for the same reason as on PayrunLine. */
  drc_vat_paid?: string;
  drc_vat_unpaid?: string;
  /** The two halves above, pre-summed. */
  total_drc_vat?: string;
}

export interface Invite {
  email?: string;
  phone?: string;
  pay_scheme?: string;
}

export interface CISReport {
  company?: any;
  uuid: string;
  tax_year: number;
  tax_month: number;
  status: string;
  hmrc_transaction: any;
  hmrc_settings_id: string;
}

export interface CIS300Submit {
  user: {
    uuid: string;
    first_name: string;
    last_name: string;
  };
  company: {
    company_id: string;
    name: string;
    trading_name: string;
  };
  sent_by: string;
  hmrc_settings_id: string;
  hmrc_transaction_id: string;
  tax_year: number;
  tax_month: number;
  ir_mark_send: string;
  ir_mark_received: string;
  sent_by_staff: boolean;
  status: string;
  updated_at: string;
  created_at: string;
}

export interface PeriodData {
  end_date: string;
  start_date: string;
  tax_week_end: number;
  tax_year_end: number;
}

export interface PayrollPayload {
  offset: number;
  limit?: number;
  pay_schedule_name?: string;
  tax_week_start_lte?: number;
  tax_week_start_gte?: number;
  tax_week_end_lte?: number;
  tax_week_end_gte?: number;
  tax_year_start_lte?: number;
  tax_year_start_gte?: number;
  tax_year_end_lte?: number;
  tax_year_end_gte?: number;
  pay_run_status?: string;
  pay_status?: string;
  outsourced?: boolean;
  order_by?: OrderByItem[];
  company_id?: string;
}

export interface PayrunPayload {
  offset?: number;
  limit?: number;
  substring?: string;
  pay_scheme?: string;
  tax_status?: string;
  id_status?: string;
  id_verified?: boolean;
  rtw_status?: string;
  cis_verified?: boolean;
  business_types?: string[];
  vat_registered?: boolean;
  order_by?: OrderByItem[];
}

export interface ReportPayload {
  tax_month?: number;
  tax_year?: number;
  offset: number;
  limit?: number;
  order_by?: OrderByItem[];
  company_id: string;
}

export interface Payslips {
  pay_run_line_id: string;
  tax_year_end: number;
  tax_week_end: number;
  pay_scheme: string;
  basic: number;
  overtime: number;
  adjustments: number;
  vat: number;
  additions: number;
  net: number;
  gross: number;
  total_pay: number;
  pay_date: string;
  company: Company;
  /** The rate this line was charged at, e.g. "0.2000". Render as a percentage; never assume 20%. */
  vat_rate: string | null;
}

export interface EnrichedPayslips extends Omit<Payslips, 'company'> {
  deductions: number;
  tax_status: string;
  total_tax: number;
  payroll_fee: number;
  company: CompanyDTO;
  /**
   * Withheld VAT, as a decimal string in major units. `vat` and `drc_vat` are never both
   * non-zero — a line is charged normally, reverse charged, or carries no VAT — so branch the
   * column on `vat_treatment` rather than on whether an amount happens to be zero.
   */
  drc_vat: string;
  /** How the line was calculated. Frozen once the run is submitted or paid. */
  vat_treatment: VAT_TREATMENTS;
}

export interface PayrollUser {
  uuid: string;
  user_id: string;
  first_name: string;
  last_name: string;
  ni_number: string;
  worker_reference: string;
  email: string;
  phone: string;
  id_verified: boolean;
  rtw_verified: boolean;
  vat_registered: boolean;
  id_status: string;
  pay_scheme: string;
  taxation_status: string;
  business_type: string;
  job_role: string;
  nationality: string;
  pay_schedule: {
    pay_schedule_id: string;
    name: string;
  };
  company: CompanyDTO;
}

export interface CISStatusData {
  status: string;
  request_doc: string;
  acknowledgement_doc: string;
  response_doc: string;
}

export interface EmailTemplate {
  name: string;
  template_id: string;
  uuid?: string;
}

export interface SMSTemplate {
  name: string;
  text: string;
  uuid?: string;
  registered_only?: boolean;
  system?: boolean;
}

export interface AgreementPaySchedule {
  pay_schedule_id: string;
  name: string;
  agreement_url?: string | null;
  medical_record_url?: string | null;
  // Which version agreement_url points at; null = a pre-versioning document, not an error.
  version?: number | null;
  // The date printed inside the contract — not the row's created_at/updated_at.
  agreement_date?: string | null;
}

export type AgreementVersionReason =
  | 'initial'
  | 'business_type_changed'
  | 'entity_data_changed'
  | 'manual_refresh'
  | 'superuser_override'
  | 'reinstated'
  | 'backfill';

export type AgreementBusinessType = 'sole-trader' | 'company' | 'partnership' | 'trust';

export interface AgreementVersion {
  version: number;
  agreement_date: string | null;
  business_type: AgreementBusinessType | null;
  pay_scheme: string | null;
  reason: AgreementVersionReason;
  note: string | null;
  created_by: string | null;
  created_at: string;
  superseded_at: string | null;
  is_current: boolean;
  s3_url: string | null;
  file_name: string | null;
  hidden_at: string | null;
  hidden_by: string | null;
  hidden_reason: string | null;
}

export interface RefreshedPayrolleeDoc {
  url: string;
  file_name: string;
  // Nullable/absent on older executions.
  version?: number | null;
}

/**
 * What every gateway export answers instead of the file itself. `url` is presigned and
 * expires in about fifteen minutes, so it must be followed immediately and never stored.
 */
export interface PresignedFile {
  file_name: string;
  url: string;
  expires_at: string;
  size_bytes: number;
}

/**
 * A pay statement send is accepted, not delivered: the sends run in Temporal after the
 * call returns and there is no progress route. `lines_pending` is an estimate read before
 * the run starts — show it as "sending N statements", never as a receipt.
 */
export interface PayslipsSendStarted {
  workflow_id: string;
  status: string;
  lines_pending: number;
}

export interface AgreementCompany {
  company_id?: string | null;
  name?: string | null;
  trading_name?: string | null;
}

export interface Agreement {
  active: boolean;
  pay_scheme: string;
  tags?: Record<string, string>;
  pay_schedule: AgreementPaySchedule;
  company: AgreementCompany;
  created_at: string;
  updated_at: string;
}

export interface PublicLink {
  url: string;
  pay_scheme: string;
}

export enum InviteLinkType {
  PRIVATE = 'private_link',
  PUBLIC = 'public_link',
}

export interface OnboardingStep {
  name: string;
  component: React.ReactNode;
  modules?: INVITE_MODULES[];
  active?: boolean;
}

export interface ActiveOnboardingStep {
  step: OnboardingStep;
  number: number;
  modules?: INVITE_MODULES[];
}

export enum PayTagGroup {
  LOCATION = 'location',
  MANAGER = 'manager',
}

export interface PayTag {
  name: string;
  group: PayTagGroup;
  uuid?: string;
  company_id?: string;
  pay_schedule_id?: string;
  verified: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Starters {
  location?: string;
  manager?: string;
}

export enum PayNotificationStatuses {
  NOT_SENT = 'not-sent',
  PARTIAL_SENT = 'partial-sent',
  ALL_SENT = 'all-sent',
}
